/**
 * DevTools Feature Configuration
 *
 * Use this file to enable/disable individual features within each DevTools tab.
 * This is useful for testing specific functionality in isolation when other
 * features are broken or incomplete.
 *
 * To toggle features at runtime, open the browser console and run:
 *   window.toggleDevFeature('player', 'teleport')
 *
 * Or import and modify directly:
 *   import { devToolsConfig } from './DevTools.config'
 *   devToolsConfig.player.features.teleport = false
 */

import { gameLogger as logger } from '../../../../utils/GameLogger';

export interface TabFeatureConfig {
  enabled: boolean;
  /**
   * Set to true to hide ALL features except those in whitelist.
   * Useful for testing individual features in isolation.
   */
  empty: boolean;
  /**
   * When empty: true, only features in this list will be shown.
   * When empty: false, this list is ignored.
   */
  whitelist: string[];
  features: Record<string, boolean>;
}

export type DevToolsTabId =
  | 'player'
  | 'physics'
  | 'classes'
  | 'npcs'
  | 'projectiles'
  | 'connections'
  | 'editor'
  | 'map'
  | 'test'
  | 'featureManager';

export const devToolsConfig: Record<DevToolsTabId, TabFeatureConfig> = {
  // ============================================
  // PLAYER TAB (👤)
  // ============================================
  player: {
    enabled: true,
    // empty:false (2026-04-30) — show all features marked `true` below.
    // Was empty:true with empty whitelist → "Showing: [none]" banner
    // hid every feature behind a console-whitelist gate. Flipped per
    // DJ's request to make dev tools usable by default.
    empty: false,
    whitelist: [],
    features: {
      // Character Stats Section
      healButton: true,
      manaButton: true,
      fullRestoreButton: true,

      // Leveling Section
      levelUpButton: true,
      addXpButton: true,
      resetLevelButton: false, // Not implemented yet

      // Teleport Section
      teleport: false, // Currently broken - disable for now
      teleportInput: false,
      teleportPresets: false,

      // Currency Section
      addGold: true,
      removeGold: true,

      // Appearance Section
      changeColor: true,
      randomizeLook: true,

      // Debug Toggles
      godMode: false, // Not implemented
      noClip: false, // Not implemented
      infiniteMana: false, // Not implemented
    }
  },

  // ============================================
  // PHYSICS TAB (⚡)
  // ============================================
  physics: {
    enabled: true,
    empty: false,
    whitelist: [],
    features: {
      // Global Physics
      gravitySlider: true,
      frictionSlider: true,
      timeScaleSlider: true,

      // Character Feel
      jumpHeightSlider: true,
      moveSpeedSlider: true,
      accelerationSlider: true,
      coyoteTimeSlider: false, // Not tested
      jumpBufferSlider: false, // Not tested

      // Physics Profiles
      saveProfile: false, // Not implemented
      loadProfile: false, // Not implemented
      presetButtons: true,

      // Advanced
      debugVisuals: true,
      showCollisionBoxes: true,
      showVelocityVectors: false, // Not implemented
    }
  },

  // ============================================
  // CLASSES TAB (⚔️)
  // ============================================
  classes: {
    enabled: true,
    empty: false,
    whitelist: [],
    features: {
      // Class Switching
      changeClass: true,
      previewClass: true,

      // Skill Testing
      testSkills: false, // Not implemented
      resetSkills: false, // Not implemented

      // Stats Allocation
      addStatPoint: true,
      autoAllocate: false, // Not implemented
      resetStats: true,
    }
  },

  // ============================================
  // NPCs TAB (🐷)
  // ============================================
  npcs: {
    enabled: true,
    empty: false,
    whitelist: [],
    features: {
      // Spawning
      spawnMob: true,
      spawnNPC: false, // Not implemented
      spawnBoss: false, // Not implemented

      // Mob Controls
      killAllMobs: true,
      respawnAll: true,
      clearMobs: true,

      // Specific Mobs
      spawnPig: true,
      spawnSlime: true,
      spawnMushroom: false, // If broken

      // Advanced
      mobAggroOverride: false, // Not implemented
      mobSpeedMultiplier: false, // Not implemented
    }
  },

  // ============================================
  // PROJECTILES TAB (🚀)
  // ============================================
  projectiles: {
    enabled: false, // Whole tab disabled - system broken
    empty: true,
    whitelist: [],
    features: {
      // Homing Settings
      homingStrength: false,
      turnRate: false,
      maxHomingDistance: false,

      // Spawn Settings
      spawnDelay: false,
      spawnOffset: false,

      // Visual
      showTrajectories: false,
      showHitboxes: false,

      // Testing
      fireTestProjectile: false,
      clearProjectiles: false,
    }
  },

  // ============================================
  // CONNECTIONS TAB (🔌)
  // ============================================
  connections: {
    enabled: true,
    empty: false,
    whitelist: [],
    features: {
      // Connection Status
      showPing: true,
      showConnectionState: true,

      // Player List
      showOtherPlayers: true,
      playerPositions: true,

      // Network Actions
      simulateLatency: false, // Not implemented
      disconnectReconnect: true,
      forceReconnect: true,

      // Debug
      showNetworkStats: false, // Not implemented
      packetLogging: false, // Not implemented
    }
  },

  // ============================================
  // EDITOR TAB (🛠️) - Not currently active
  // ============================================
  editor: {
    enabled: false,
    empty: true,
    whitelist: [],
    features: {
      // Platform Tools
      addPlatform: false,
      deletePlatform: false,
      modifyPlatform: false,

      // Spawn Points
      setPlayerSpawn: false,
      setMobSpawn: false,

      // Map IO
      saveMap: false,
      loadMap: false,
      exportJSON: false,

      // Admin
      clearAllPlatforms: false,
    }
  },

  // ============================================
  // MAP TAB (🗺️) - Not currently active
  // ============================================
  map: {
    enabled: false,
    empty: true,
    whitelist: [],
    features: {
      // Overview
      showLevelInfo: false,
      platformCount: false,
      spawnPointList: false,

      // Teleporters
      teleporterList: false,
      editTeleporter: false,

      // Navigation
      jumpToPlatform: false,
      highlightPlatform: false,
    }
  },

  // ============================================
  // TEST TAB (🧪) - Not currently active
  // ============================================
  test: {
    enabled: false,
    empty: true,
    whitelist: [],
    features: {
      // Scenarios
      runAllScenarios: false,
      movementTest: false,
      combatTest: false,
      physicsTest: false,

      // Performance
      benchmark: false,
      stressTest: false,
      memoryProfile: false,

      // Results
      showTestReport: false,
      exportResults: false,
    }
  },

  // ============================================
  // FEATURE MANAGER TAB (🚦) - Meta configuration
  // ============================================
  featureManager: {
    enabled: true,
    empty: false,
    whitelist: [],
    features: {
      showPanel: true,
      showFullManager: true,
    }
  }
};

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Check if a tab should render empty (for testing)
 */
export function isTabEmpty(tab: DevToolsTabId): boolean {
  return devToolsConfig[tab]?.empty ?? false;
}

/**
 * Set a tab to empty mode (for testing)
 */
export function setTabEmpty(tab: DevToolsTabId, empty: boolean): void {
  if (devToolsConfig[tab]) {
    const previousState = devToolsConfig[tab].empty;
    devToolsConfig[tab].empty = empty;
    const whitelisted = devToolsConfig[tab].whitelist;

    logger.info('devtools', `Tab '${tab}' ${empty ? 'set to EMPTY mode' : 'restored'}`, {
      tab,
      previousState,
      newState: empty,
      whitelist: whitelisted
    });
  }
}

/**
 * Add a feature to the whitelist (for testing)
 */
export function whitelistFeature(tab: DevToolsTabId, feature: string): void {
  if (devToolsConfig[tab]) {
    const previousWhitelist = [...devToolsConfig[tab].whitelist];
    if (!devToolsConfig[tab].whitelist.includes(feature)) {
      devToolsConfig[tab].whitelist.push(feature);
    }

    logger.info('devtools', `Added '${feature}' to '${tab}' whitelist`, {
      tab,
      feature,
      previousWhitelist,
      newWhitelist: devToolsConfig[tab].whitelist,
      isEmptyMode: devToolsConfig[tab].empty
    });
  }
}

/**
 * Remove a feature from the whitelist
 */
export function unwhitelistFeature(tab: DevToolsTabId, feature: string): void {
  if (devToolsConfig[tab]) {
    const previousWhitelist = [...devToolsConfig[tab].whitelist];
    devToolsConfig[tab].whitelist = devToolsConfig[tab].whitelist.filter(f => f !== feature);

    logger.info('devtools', `Removed '${feature}' from '${tab}' whitelist`, {
      tab,
      feature,
      previousWhitelist,
      newWhitelist: devToolsConfig[tab].whitelist
    });
  }
}

/**
 * Set the entire whitelist for a tab
 */
export function setWhitelist(tab: DevToolsTabId, features: string[]): void {
  if (devToolsConfig[tab]) {
    const previousWhitelist = [...devToolsConfig[tab].whitelist];
    devToolsConfig[tab].whitelist = [...features];

    logger.info('devtools', `Set '${tab}' whitelist`, {
      tab,
      previousWhitelist,
      newWhitelist: features
    });
  }
}

/**
 * Get current whitelist for a tab
 */
export function getWhitelist(tab: DevToolsTabId): string[] {
  return devToolsConfig[tab]?.whitelist ?? [];
}

/**
 * Check if a feature should be visible
 *
 * When tab.empty is true, only whitelisted features are shown.
 * When tab.empty is false, all features with value true are shown.
 */
export function isFeatureEnabled(tab: DevToolsTabId, feature: string): boolean {
  const tabConfig = devToolsConfig[tab];
  if (!tabConfig || !tabConfig.enabled) {
    logger.debug('devtools', `isFeatureEnabled('${tab}', '${feature}') = false (tab disabled or not found)`);
    return false;
  }

  // In empty mode, only show whitelisted features
  if (tabConfig.empty) {
    const isWhitelisted = tabConfig.whitelist.includes(feature);
    logger.debug('devtools', `isFeatureEnabled('${tab}', '${feature}') = ${isWhitelisted}`, {
      mode: 'whitelist',
      whitelist: tabConfig.whitelist
    });
    return isWhitelisted;
  }

  // Normal mode - check features object
  const isEnabled = tabConfig.features[feature] ?? false;
  logger.debug('devtools', `isFeatureEnabled('${tab}', '${feature}') = ${isEnabled} (normal mode)`);
  return isEnabled;
}

/**
 * Check if an entire tab is enabled
 */
export function isTabEnabled(tab: DevToolsTabId): boolean {
  return devToolsConfig[tab]?.enabled ?? false;
}

/**
 * Toggle a feature at runtime
 */
export function toggleFeature(tab: DevToolsTabId, feature: string): boolean {
  const tabConfig = devToolsConfig[tab];
  if (!tabConfig) return false;

  const current = tabConfig.features[feature] ?? false;
  tabConfig.features[feature] = !current;

  logger.info('devtools', `Toggled feature '${tab}.${feature}'`, {
    tab,
    feature,
    previousState: current,
    newState: !current
  });
  return !current;
}

/**
 * Enable/disable an entire tab
 */
export function setTabEnabled(tab: DevToolsTabId, enabled: boolean): void {
  if (devToolsConfig[tab]) {
    const previousState = devToolsConfig[tab].enabled;
    devToolsConfig[tab].enabled = enabled;

    logger.info('devtools', `Tab '${tab}' ${enabled ? 'enabled' : 'disabled'}`, {
      tab,
      previousState,
      newState: enabled
    });
  }
}

/**
 * Get list of enabled features for a tab
 */
export function getEnabledFeatures(tab: DevToolsTabId): string[] {
  const tabConfig = devToolsConfig[tab];
  if (!tabConfig || !tabConfig.enabled) return [];

  return Object.entries(tabConfig.features)
    .filter(([_, enabled]) => enabled)
    .map(([name]) => name);
}

/**
 * Get list of disabled features for a tab
 */
export function getDisabledFeatures(tab: DevToolsTabId): string[] {
  const tabConfig = devToolsConfig[tab];
  if (!tabConfig) return [];

  return Object.entries(tabConfig.features)
    .filter(([_, enabled]) => !enabled)
    .map(([name]) => name);
}

// Expose to window for console debugging
if (typeof window !== 'undefined') {
  (window as any).devToolsConfig = devToolsConfig;
  (window as any).toggleDevFeature = toggleFeature;
  (window as any).setDevTabEnabled = setTabEnabled;
  (window as any).setTabEmpty = setTabEmpty;
  (window as any).whitelistFeature = whitelistFeature;
  (window as any).unwhitelistFeature = unwhitelistFeature;
  (window as any).setWhitelist = setWhitelist;
  (window as any).getWhitelist = getWhitelist;
  (window as any).listEnabledFeatures = getEnabledFeatures;
  (window as any).listDisabledFeatures = getDisabledFeatures;

  logger.info('devtools', '[DevTools] Config loaded. Available commands:');
  logger.info('devtools', '  setTabEmpty("player", true) - Enable empty mode (shows only whitelisted)');
  logger.info('devtools', '  whitelistFeature("player", "healButton") - Add feature to whitelist');
  logger.info('devtools', '  unwhitelistFeature("player", "healButton") - Remove from whitelist');
  logger.info('devtools', '  setWhitelist("player", ["healButton", "manaButton"]) - Set all whitelisted');
  logger.info('devtools', '  getWhitelist("player") - See current whitelist');
  logger.info('devtools', '  toggleDevFeature("player", "teleport") - Toggle feature in normal mode');
  logger.info('devtools', '  setDevTabEnabled("npcs", false) - Enable/disable entire tab');
}
